import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  FiAward,
  FiBriefcase,
  FiChevronDown,
  FiChevronRight,
  FiFileText,
  FiGrid,
  FiHome,
  FiInfo,
  FiLogOut,
  FiMail,
  FiMenu,
  FiPackage,
  FiShoppingCart,
  FiX,
} from 'react-icons/fi';
import '../csscomponent/Header.css';
import logo from '../assest/logo.svg';
import { useAuth } from '../lib/authSession';
import { getCart, getHeaderMenu } from '../lib/sqlAuth';
import { subscribeCmsUpdates } from '../lib/cmsSync';
import { hideHomeBannerAd, resumeHomeBannerAd } from '../lib/admobService';
import CartDrawer from './cart/CartDrawer';

const FALLBACK_MENU_ITEMS = [
  { id: 'home', label: 'Home', url: '/', target: '_self', isEnabled: true },
  { id: 'services', label: 'Services', url: '/services', target: '_self', isEnabled: true },
  { id: 'career', label: 'Career', url: '/career/job-apply', target: '_self', isEnabled: true },
  { id: 'brochure', label: 'Brochure', url: '/brochure', target: '_self', isEnabled: true },
  { id: 'product', label: 'Product', url: '/product', target: '_self', isEnabled: true },
  { id: 'about', label: 'About', url: '/about', target: '_self', isEnabled: true },
  { id: 'contact', label: 'Contact Us', url: '/contact', target: '_self', isEnabled: true },
];

function setMobileNavOpenState(isOpen) {
  if (typeof document === 'undefined') return;
  document.body.classList.toggle('mobile-nav-open', isOpen);
  window.dispatchEvent(new CustomEvent('sd:mobile-nav-toggle', { detail: { open: isOpen } }));
}

function isExternalUrl(url = '') {
  return /^(https?:)?\/\//i.test(String(url)) || String(url).startsWith('mailto:') || String(url).startsWith('tel:');
}

function normalizeMenuUrl(url = '') {
  const nextUrl = String(url || '/').trim() || '/';
  return nextUrl === '/' ? '/home' : nextUrl;
}

function hasStoredAuthState() {
  if (typeof window === 'undefined') return false;

  try {
    return localStorage.getItem('sd_auth') === 'true' || Boolean(localStorage.getItem('sd_auth_user'));
  } catch (_) {
    return false;
  }
}

function getMenuIcon(label = '') {
  const key = String(label).trim().toLowerCase();
  if (key.includes('home')) return FiHome;
  if (key.includes('service')) return FiGrid;
  if (key.includes('career')) return FiBriefcase;
  if (key.includes('brochure')) return FiFileText;
  if (key.includes('product')) return FiPackage;
  if (key.includes('about')) return FiInfo;
  if (key.includes('contact')) return FiMail;
  return FiGrid;
}

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [careerOpen, setCareerOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 900 : false
  );
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);
  const toggleRef = useRef(null);
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const hasAuthState = isAuthenticated || Boolean(user) || hasStoredAuthState();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch CMS header items
  useEffect(() => {
    let mounted = true;
    const loadMenu = () => {
      getHeaderMenu()
        .then((res) => {
          if (!mounted) return;
          const menus = Array.isArray(res?.menus) ? res.menus : [];
          const header = menus.find((menu) => menu.locationKey === 'header') || menus[0];
          setMenuItems(Array.isArray(header?.items) ? header.items : []);
        })
        .catch(() => {});
    };

    loadMenu();
    const unsubscribe = subscribeCmsUpdates(() => loadMenu());

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [isAuthenticated, isAdmin]);

  // Load cart item count for ALL users (authenticated or visitors)
  useEffect(() => {
    let mounted = true;
    const loadCartCount = () => {
      getCart()
        .then((res) => {
          if (!mounted) return;
          const totalItems = (res?.items || []).reduce(
            (sum, item) => sum + Number(item.quantity || 0),
            0
          );
          setCartCount(totalItems);
        })
        .catch(() => {
          if (mounted) {
            setCartCount(0);
          }
        });
    };

    loadCartCount();
    const handleCartUpdate = () => loadCartCount();
    window.addEventListener('sd:cart-updated', handleCartUpdate);

    return () => {
      mounted = false;
      window.removeEventListener('sd:cart-updated', handleCartUpdate);
    };
  }, [location.pathname]);

  // Handle body scroll lock & ad pause when drawer or cart is open
  useEffect(() => {
    const isAnyOpen = menuOpen || cartOpen;
    setMobileNavOpenState(menuOpen);

    if (isAnyOpen) {
      document.body.style.overflow = 'hidden';
      hideHomeBannerAd();
    } else {
      document.body.style.overflow = '';
      resumeHomeBannerAd();
    }

    return () => {
      document.body.style.overflow = '';
      resumeHomeBannerAd();
      setMobileNavOpenState(false);
    };
  }, [menuOpen, cartOpen]);

  // Close menus on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setCartOpen(false);
        setCareerOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close on route change
  useEffect(() => {
    setMenuOpen(false);
    setCartOpen(false);
    setCareerOpen(false);
  }, [location.pathname]);

  const closeMenu = () => {
    setMenuOpen(false);
    setCareerOpen(false);
  };

  const handleOpenCart = () => {
    closeMenu();
    setCartOpen(true);
  };

  const handleToggleMenu = () => {
    setCartOpen(false);
    setMenuOpen((prev) => !prev);
  };

  const navigateToUrl = (url = '/', target = '_self') => {
    const nextUrl = normalizeMenuUrl(url);
    if (isExternalUrl(nextUrl) || target === '_blank') {
      window.open(nextUrl, target || '_self', 'noopener,noreferrer');
      closeMenu();
      return;
    }

    const [pathPart, hashPart = ''] = nextUrl.split('#');
    const normalizedPath = pathPart || '/';
    const isSamePath = normalizedPath === location.pathname;
    closeMenu();

    if (isSamePath) {
      if (hashPart) {
        const targetElement = document.getElementById(hashPart);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
      window.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }

    navigate(nextUrl);
  };

  const handleLogout = async () => {
    await logout();
    closeMenu();
    navigate('/home');
  };

  const userInitial = (user?.name || 'U').trim().charAt(0).toUpperCase();

  const visibleMenuItems = useMemo(() => {
    const source = menuItems.length > 0 ? menuItems : FALLBACK_MENU_ITEMS;
    return source.filter((item) => {
      if (item.isEnabled === false) return false;
      const label = String(item.label || '').trim().toLowerCase();
      const url = String(item.url || '').trim().toLowerCase();

      // On mobile where bottom bar already has Home, avoid repeating Home
      if (isMobile && (label === 'home' || url === '/' || url === '/home')) {
        return false;
      }

      if (hasAuthState && (label === 'login' || label === 'signup' || label === 'sign up')) {
        return false;
      }
      return true;
    });
  }, [menuItems, hasAuthState, isMobile]);

  const isCareerRoute = location.pathname.startsWith('/career');

  return (
    <>
      {/* Shopping Cart Drawer Component */}
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

      {/* Mobile Drawer Backdrop */}
      {menuOpen && (
        <div
          className="mobile-drawer-backdrop"
          onClick={closeMenu}
          aria-label="Close navigation"
        />
      )}

      {/* Mobile Side Drawer Panel (Rendered independently from navbar stacking context) */}
      <aside
        ref={navRef}
        className={`mobile-nav-drawer ${menuOpen ? 'active' : ''}`}
        aria-label="Navigation menu"
        aria-hidden={!menuOpen}
      >
        <div className="mobile-drawer-header">
          <button
            type="button"
            className="mobile-drawer-brand-btn"
            onClick={() => navigateToUrl('/home')}
          >
            <img src={logo} alt="Logo" className="logo" />
            <span className="brand-name">Services and Developer</span>
          </button>
          <button
            type="button"
            className="mobile-drawer-close-btn"
            onClick={closeMenu}
            aria-label="Close navigation menu"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>

        <div className="mobile-drawer-body">
          <nav className="mobile-drawer-links" aria-label="Mobile Navigation Links">
            {visibleMenuItems.map((item) => {
              const label = String(item.label || '').toLowerCase().trim();
              if (label === 'career') {
                return (
                  <div key={item.id || item.label} className={`career-dropdown-wrap ${careerOpen ? 'open' : ''}`}>
                    <button
                      type="button"
                      className={`career-trigger-btn nav-link-button ${isCareerRoute ? 'active' : ''} ${careerOpen ? 'expanded' : ''}`}
                      onClick={() => setCareerOpen((prev) => !prev)}
                      aria-expanded={careerOpen}
                    >
                      <div className="career-trigger-left">
                        <FiBriefcase className="nav-link-icon" aria-hidden="true" />
                        <span>Career</span>
                      </div>
                      <FiChevronDown className={`career-chevron ${careerOpen ? 'rotated' : ''}`} aria-hidden="true" />
                    </button>

                    {careerOpen && (
                      <div className="career-dropdown-menu show">
                        <button
                          type="button"
                          className={`career-sub-item ${location.pathname === '/career/job-apply' ? 'active' : ''}`}
                          onClick={() => navigateToUrl('/career/job-apply')}
                        >
                          <div className="career-sub-left">
                            <div className="career-sub-icon apply-icon">
                              <FiFileText aria-hidden="true" />
                            </div>
                            <div className="career-sub-text">
                              <span className="career-sub-title">Job Apply</span>
                              <span className="career-sub-desc">Explore open developer roles</span>
                            </div>
                          </div>
                          <FiChevronRight className="career-sub-arrow" aria-hidden="true" />
                        </button>

                        <button
                          type="button"
                          className={`career-sub-item ${location.pathname === '/career/internship-verification' ? 'active' : ''}`}
                          onClick={() => navigateToUrl('/career/internship-verification')}
                        >
                          <div className="career-sub-left">
                            <div className="career-sub-icon verify-icon">
                              <FiAward aria-hidden="true" />
                            </div>
                            <div className="career-sub-text">
                              <span className="career-sub-title">Internship Verification</span>
                              <span className="career-sub-desc">Verify your certificate</span>
                            </div>
                          </div>
                          <FiChevronRight className="career-sub-arrow" aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <button
                  key={item.id || item.label}
                  type="button"
                  className={`nav-link-button ${location.pathname === (item.url || '/') ? 'active' : ''}`}
                  onClick={() => navigateToUrl(item.url || '/', item.target || '_self')}
                >
                  {(() => {
                    const Icon = getMenuIcon(item.label);
                    return <Icon aria-hidden="true" className="nav-link-icon" />;
                  })()}
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="mobile-drawer-footer">
            {!hasAuthState ? (
              <div className="mobile-drawer-auth-actions">
                <button
                  type="button"
                  className="btn outline"
                  onClick={() => navigateToUrl('/login')}
                >
                  Login
                </button>
                <button
                  type="button"
                  className="btn filled"
                  onClick={() => navigateToUrl('/signup')}
                >
                  Signup
                </button>
              </div>
            ) : (
              <div className="mobile-drawer-user-panel">
                <div className="mobile-drawer-user-card">
                  <div className="mobile-drawer-avatar">
                    {user?.avatarUrl ? (
                      <img src={user.avatarUrl} alt={user?.name || 'User'} />
                    ) : (
                      <span>{userInitial}</span>
                    )}
                  </div>
                  <div className="mobile-drawer-user-info">
                    <strong className="mobile-drawer-user-name">{user?.name || 'User'}</strong>
                    <span className="mobile-drawer-user-email">{user?.email || ''}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="mobile-signout-btn"
                  onClick={handleLogout}
                  aria-label="Logout"
                >
                  <FiLogOut aria-hidden="true" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Top Navigation Bar */}
      <header className="navbar">
        <button
          type="button"
          className="logo-container logo-button"
          onClick={() => navigateToUrl('/home')}
          aria-label="Services and Developer Home"
        >
          <img src={logo} alt="Logo" className="logo" />
          <span className="brand-name">Services and Developer</span>
        </button>

        {/* Desktop Navigation Links */}
        <div className="desktop-nav-content">
          <nav className="nav-links">
            {visibleMenuItems.map((item) => {
              const label = String(item.label || '').toLowerCase().trim();
              if (label === 'career') {
                return (
                  <div
                    key={item.id || item.label}
                    className={`career-dropdown-wrap ${careerOpen ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className={`career-trigger-btn nav-link-button ${isCareerRoute ? 'active' : ''}`}
                      onClick={() => setCareerOpen((prev) => !prev)}
                      aria-expanded={careerOpen}
                    >
                      <span>Career</span>
                      <FiChevronDown className={`career-chevron ${careerOpen ? 'rotated' : ''}`} aria-hidden="true" />
                    </button>

                    {careerOpen && (
                      <div className="career-dropdown-menu show">
                        <button
                          type="button"
                          className="career-sub-item"
                          onClick={() => navigateToUrl('/career/job-apply')}
                        >
                          <span>Job Apply</span>
                        </button>
                        <button
                          type="button"
                          className="career-sub-item"
                          onClick={() => navigateToUrl('/career/internship-verification')}
                        >
                          <span>Internship Verification</span>
                        </button>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <button
                  key={item.id || item.label}
                  type="button"
                  className={`nav-link-button ${location.pathname === (item.url || '/') ? 'active' : ''}`}
                  onClick={() => navigateToUrl(item.url || '/', item.target || '_self')}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div className="nav-buttons">
            <button
              type="button"
              className="cart-chip desktop-cart-chip"
              onClick={handleOpenCart}
              aria-label={`Open shopping cart with ${cartCount} items`}
            >
              <FiShoppingCart className="cart-chip-icon" />
              <span className="cart-chip-text">Cart</span>
              {cartCount > 0 ? <span className="cart-chip-badge">{cartCount}</span> : null}
            </button>

            {!hasAuthState ? (
              <>
                <NavLink to="/login" className="btn outline">
                  Login
                </NavLink>
                <NavLink to="/signup" className="btn filled">
                  Signup
                </NavLink>
              </>
            ) : (
              <div className="user-menu desktop-user-menu">
                <button
                  type="button"
                  className="user-chip"
                  onClick={() => navigate('/dashboard/profile')}
                  aria-label="User account profile"
                >
                  {user?.avatarUrl ? (
                    <img src={user.avatarUrl} alt={user?.name || 'User'} className="user-avatar-image" />
                  ) : (
                    <span className="user-avatar-fallback">{userInitial}</span>
                  )}
                  <span className="user-name">{user?.name || 'User'}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Header Actions (Cart + Hamburger) */}
        <div className="header-mobile-actions">
          <button
            type="button"
            className="mobile-header-cart"
            onClick={handleOpenCart}
            aria-label={`Open shopping cart with ${cartCount} items`}
          >
            <FiShoppingCart aria-hidden="true" />
            {cartCount > 0 ? <span className="cart-badge-pill">{cartCount}</span> : null}
          </button>

          <button
            ref={toggleRef}
            type="button"
            className={`hamburger ${menuOpen ? 'open' : ''}`}
            onClick={handleToggleMenu}
            aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
          </button>
        </div>
      </header>
    </>
  );
}

export default Header;
